/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import {
  forwardRef,
  ForwardedRef,
  useRef,
  useImperativeHandle,
  CSSProperties
} from 'preact/compat';
import { useBareButton, type BareButtonProps } from '../hooks/UNSAFE_useBareButton';

import type { UnsafeDomHandle } from '../utils/UNSAFE_unsafeDomAccess';
import { UNSAFE_DOM_ACCESS } from '../utils/UNSAFE_unsafeDomAccess';

type ButtonHandle = {
  focus: () => void;
  blur: () => void;
  click: () => void;
} & UnsafeDomHandle;

type ButtonStylingProps = {
  /**
   * The classes to be applied
   */
  classNames?: string;

  /**
   * The styles to be applied
   */
  style?: CSSProperties;
};

/**
 * BareButtons are unformatted bare buttons that direct users to initiate or take actions and work with a single tap, click, or keystroke.
 */
export const BareButton = forwardRef(
  (props: BareButtonProps & ButtonStylingProps, ref: ForwardedRef<ButtonHandle>) => {
    const rootRef = useRef<
      HTMLButtonElement & HTMLAnchorElement & HTMLSpanElement & HTMLDivElement
    >(null);

    useImperativeHandle(
      ref,
      () => {
        return {
          focus: () => {
            rootRef.current?.focus();
          },
          blur: () => {
            rootRef.current?.blur();
          },
          click: () => {
            rootRef.current?.click();
          },
          [UNSAFE_DOM_ACCESS]: rootRef.current!
        };
      },
      []
    );

    const { style: buttonStyle, classNames: buttonClass, ...rest } = { ...props };
    const buttonProps = useBareButton({ ...rest });
    const ElementType = props.elementDetails ? props.elementDetails.type : 'button';

    return (
      <ElementType ref={rootRef} style={buttonStyle} class={buttonClass} {...buttonProps}>
        {props.children}
      </ElementType>
    );
  }
);

BareButton.displayName = 'BareButton';
